import React from 'react';
import { useTranslation } from 'react-i18next';
import { withI18nProvider } from '../../common/i18n';
import { BraintreeDropIn, usePrepareDropIn } from '../../components/BraintreeDropIn';
import { PaymentErrorDialog } from '../../components/PaymentErrorDialog';
import { withSnackbarProvider } from '../../components/GlobalSnackbar';
import { RedeemCoupon } from './RedeemCoupon/RedeemCoupon';
import { useCheckout } from './useCheckout';
import { usePrice } from './usePrice';
import { PurchaserForm, WhatsNext } from '../common';
import { PurchaseInfo } from './models';
import { GroupOrderInfo } from "./GroupOrderInfo";

interface IProps {
  shortLink: string;
  productName: string;
  price: string;
  onPurchase: (purchaseInfo: PurchaseInfo) => void;
  onPaymentMethodSelected?: (type: 'CreditCard' | 'PayPalAccount') => void;
}

export const PaymentCheckoutPage = withI18nProvider(
  withSnackbarProvider(({ shortLink, productName, price, onPurchase, onPaymentMethodSelected }: IProps) => {
    const { t } = useTranslation();

    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const { applyDiscount, clearDiscount, currentPrice } = usePrice(price);

    const [braintreeServiceRef, canSubmit, preparationError] = usePrepareDropIn(
      currentPrice.currentPrice,
      productName,
      {
        container: containerRef,
        translations: {
          chooseAWayToPay: t('checkout.purchaser_form.choose_a_way_to_pay'),
          chooseAnotherWayToPay: t('checkout.purchaser_form.choose_another_way_to_pay'),
        },
        onPaymentMethodSelected,
      }
    );

    const { paymentError, loadingPayment, onSubmit } = useCheckout(
      braintreeServiceRef.current,
      shortLink,
      currentPrice,
      onPurchase
    );

    return (
      <div>
        <PurchaserForm
          loading={loadingPayment}
          submitDisabled={!canSubmit}
          onSubmit={onSubmit}
          price={price}
          reducedPrice={currentPrice.appliedDiscount?.reducedPrice ?? null}
        >
          <RedeemCoupon
            shortLink={shortLink}
            onSuccess={applyDiscount}
            onClear={clearDiscount}
            activeCode={currentPrice.appliedDiscount?.code}
          />
          <BraintreeDropIn disabled={parseFloat(currentPrice.currentPrice) === 0} ref={containerRef} />
        </PurchaserForm>
        <GroupOrderInfo shortLink={shortLink} />
        <WhatsNext
          items={[
            t('checkout.whats_next.tour_items.li_1'),
            t('checkout.whats_next.tour_items.li_2'),
            t('checkout.whats_next.tour_items.li_3'),
          ]}
        />
        <PaymentErrorDialog error={preparationError ?? paymentError ?? undefined} />
      </div>
    );
  })
);
